"use server";

import { prisma } from "../../lib/prisma";
import { auth } from "../../lib/auth";
import { dbg } from "../../lib/debug-log";
import { parseMentions } from "../../lib/mentions";
import { broadcastToAllClients } from "../../lib/arena-broadcast";
import { dispatchNotificationToUser } from "../../lib/notify";
import { awardPoints, hasReceivedPointToday } from "../../lib/points";
import { CHAT_MESSAGE_MAX_LENGTH, CHAT_PAGE_SIZE, type ChatMessageView } from "../../lib/chat-types";

async function toView(
  m: {
    id: string; userId: string; body: string; createdAt: Date; deletedAt: Date | null;
    user: { displayName: string };
    likes: Array<{ userId: string }>;
  },
  viewerId: string
): Promise<ChatMessageView> {
  return {
    id: m.id,
    userId: m.userId,
    displayName: m.user.displayName || "Someone",
    body: m.deletedAt ? "" : m.body,
    createdAt: m.createdAt.toISOString(),
    deleted: !!m.deletedAt,
    likeCount: m.likes.length,
    likedByMe: m.likes.some((l) => l.userId === viewerId),
  };
}

// Most recent CHAT_PAGE_SIZE messages, oldest-first for natural reading order.
export async function getRecentChatMessages(): Promise<ChatMessageView[]> {
  const session = await auth();
  if (!session) throw new Error("Not authenticated");

  const messages = await prisma.chatMessage.findMany({
    orderBy: { createdAt: "desc" },
    take: CHAT_PAGE_SIZE,
    include: { user: { select: { displayName: true } }, likes: { select: { userId: true } } },
  });

  const views = await Promise.all(messages.map((m) => toView(m, session.user.id)));
  return views.reverse();
}

// Per-account, server-persisted "have I seen the latest message" marker —
// survives device switches, unlike a localStorage last-seen approach. Called
// once when the chat tab is actually viewed (not just when the panel opens
// on the Online tab) — see ArenaChatPanel's onChatTabViewed callback.
export async function markChatRead(): Promise<void> {
  const session = await auth();
  if (!session) throw new Error("Not authenticated");
  await prisma.user.update({
    where: { id: session.user.id },
    data: { lastReadChatAt: new Date() },
  });
}

// Initial unread-state computation for a fresh page load — compares the
// session user's lastReadChatAt against the newest message's createdAt.
// Subsequent live updates are tracked client-side against incoming
// chat_message SSE events (see global-ticker-client.tsx); this is only the
// seed value for first paint / a fresh device.
export async function getHasUnreadChat(): Promise<boolean> {
  const session = await auth();
  if (!session) return false;

  const [user, latest] = await Promise.all([
    prisma.user.findUnique({ where: { id: session.user.id }, select: { lastReadChatAt: true } }),
    prisma.chatMessage.findFirst({ orderBy: { createdAt: "desc" }, select: { createdAt: true } }),
  ]);

  if (!latest) return false;
  if (!user?.lastReadChatAt) return true;
  return latest.createdAt > user.lastReadChatAt;
}

export async function sendChatMessage(body: string): Promise<{ ok: true; id: string } | { ok: false; error: string }> {
  const session = await auth();
  if (!session) throw new Error("Not authenticated");

  const trimmed = body.trim();
  if (!trimmed) return { ok: false, error: "Empty message" };
  if (trimmed.length > CHAT_MESSAGE_MAX_LENGTH) {
    return { ok: false, error: `Message too long (max ${CHAT_MESSAGE_MAX_LENGTH} characters)` };
  }

  const [sender, allUsers] = await Promise.all([
    prisma.user.findUnique({ where: { id: session.user.id }, select: { displayName: true } }),
    prisma.user.findMany({ select: { id: true, displayName: true } }),
  ]);
  const displayName = sender?.displayName || "Someone";

  const message = await prisma.chatMessage.create({
    data: { userId: session.user.id, body: trimmed },
  });

  dbg.info("chat", `chat message sent by ${displayName}`, { userId: session.user.id, messageId: message.id });

  // Daily-capped activity point for using chat at all — infra already built
  // in Phase 5 (lib/points.ts) ahead of this feature existing.
  hasReceivedPointToday(session.user.id, "CHAT_USE").then((already) => {
    if (!already) {
      awardPoints(session.user.id, "CHAT_USE", { refId: message.id }).catch((err) =>
        dbg.error("kudos", "awardPoints(CHAT_USE) threw", { userId: session.user.id, error: String(err) })
      );
    }
  }).catch((err) => dbg.warn("chat", "hasReceivedPointToday check failed", { error: String(err) }));

  // Broadcast the new message itself to every connected client (drives the
  // live chat view) over the existing system-wide SSE channel — same channel
  // /shout and the maintenance warnings already use, no new transport.
  broadcastToAllClients({
    type: "chat_message",
    id: message.id,
    userId: session.user.id,
    displayName,
    body: trimmed,
    serverTs: message.createdAt.toISOString(),
  }).catch((err) => dbg.warn("chat", "chat_message broadcast failed", { error: String(err) }));

  // @mentions: notify each mentioned user (off-app channel per their pref)
  // AND broadcast a dedicated, per-target "mention" event so their ticker
  // surfaces it specifically — every connected client receives this event
  // (it rides the same system channel) but only the targeted user's client
  // renders it, per the v1 client-side-filtering design.
  const mentions = parseMentions(trimmed, allUsers.filter((u) => u.id !== session.user.id));
  for (const mention of mentions) {
    broadcastToAllClients({
      type: "mention",
      targetUserId: mention.userId,
      fromUserId: session.user.id,
      fromDisplayName: displayName,
      body: trimmed,
      serverTs: message.createdAt.toISOString(),
    }).catch((err) => dbg.warn("chat", "mention broadcast failed", { error: String(err), targetUserId: mention.userId }));

    // Catch-up row for this one recipient — mirrors lib/notify.ts's
    // deliverOne(), but written directly here since mention bypasses that
    // chokepoint (it has its own dedicated broadcast above already).
    prisma.tickerEvent.create({
      data: {
        userId: mention.userId,
        kind: "mention",
        payload: JSON.stringify({ icon: "💬", text: `${displayName} mentioned you: ${trimmed}`, url: "/vote" }),
      },
    }).catch((err) => dbg.warn("chat", "mention TickerEvent create failed", { error: String(err), targetUserId: mention.userId }));

    dispatchNotificationToUser(mention.userId, "mention", {
      push: {
        title: `${displayName} mentioned you`,
        body: trimmed.slice(0, 120),
        url: "/vote",
        tag: "bnc-mention",
      },
      email: {
        subject: `${displayName} mentioned you in chat`,
        text: `${displayName} mentioned you: "${trimmed}". Head to ${process.env.NEXTAUTH_URL ?? ""}/vote and tap the online count to reply.`,
        html: `<p><strong>${displayName}</strong> mentioned you: "${trimmed}"</p><p>Head to the arena and tap the online count to reply.</p>`,
      },
    }).catch((err) => dbg.error("notify", "mention dispatch failed", { error: String(err), targetUserId: mention.userId }));
  }

  return { ok: true, id: message.id };
}

// Double-tap toggles: like if not yet liked, unlike if already liked.
export async function toggleChatLike(messageId: string): Promise<{ liked: boolean; likeCount: number }> {
  const session = await auth();
  if (!session) throw new Error("Not authenticated");

  const existing = await prisma.chatMessageLike.findUnique({
    where: { messageId_userId: { messageId, userId: session.user.id } },
  });

  if (existing) {
    await prisma.chatMessageLike.delete({ where: { id: existing.id } });
  } else {
    await prisma.chatMessageLike.create({ data: { messageId, userId: session.user.id } });
  }

  const likeCount = await prisma.chatMessageLike.count({ where: { messageId } });

  broadcastToAllClients({
    type: "chat_like",
    messageId,
    likeCount,
    serverTs: new Date().toISOString(),
  }).catch((err) => dbg.warn("chat", "chat_like broadcast failed", { error: String(err) }));

  return { liked: !existing, likeCount };
}

// Admin-only soft delete — the row stays (audit trail) but body is blanked
// and it renders as a placeholder client-side. Per explicit decision: chat
// history is kept forever, but admins need a removal lever for a family app
// with kids potentially present.
export async function deleteChatMessage(messageId: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const session = await auth();
  if (!session || session.user.role !== "ADMIN") return { ok: false, error: "Unauthorized" };

  await prisma.chatMessage.update({
    where: { id: messageId },
    data: { deletedAt: new Date(), deletedBy: session.user.id },
  });

  dbg.info("chat", `message deleted by admin ${session.user.email}`, { messageId });

  broadcastToAllClients({
    type: "chat_deleted",
    messageId,
    serverTs: new Date().toISOString(),
  }).catch((err) => dbg.warn("chat", "chat_deleted broadcast failed", { error: String(err) }));

  return { ok: true };
}
